// Reine Hilfsfunktionen der Select-Komponente, ohne Zugriff auf den Komponentenzustand

export const APPLY_MODES = ['immediate', 'button'];
export const SORT_MODES = ['none', 'alphabetisch'];
export const SIZES = ['sm', 'md', 'lg'];

// Wandelt einen Wert in einen Text, null und undefined ergeben einen leeren Text
export function asText(value) {
  return value === null || value === undefined ? '' : String(value);
}

export function toValueArray(value) {
  if (Array.isArray(value)) {
    return value.slice();
  }
  return value === null || value === undefined ? [] : [value];
}

// Entfernt diakritische Zeichen und vereinheitlicht auf Kleinbuchstaben
function normalizeText(text) {
  return asText(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

// Bringt eine einzelne Option auf die interne Form, akzeptiert Objekte sowie Strings und Zahlen
function normalizeOption(raw, keyPrefix, { textField, valueField }) {
  const isObject = raw !== null && typeof raw === 'object';
  const value = isObject ? raw[valueField] : raw;
  const text = isObject ? raw[textField] : raw;
  const hasNoText = text === null || text === undefined;
  return {
    isGroup: false,
    value,
    text: hasNoText ? asText(value) : asText(text),
    disabled: isObject ? Boolean(raw.disabled) : false,
    key: `${keyPrefix}|${String(value)}`,
    raw,
  };
}

// Vereinheitlicht Optionsobjekte und einfache Werte auf { value, text, disabled, key, raw }
// Gruppen werden zu einer flachen Liste, in der jeder Gruppentitel als isGroup-Eintrag steht
export function buildOptions(options, config) {
  const { groupLabel, groupValues } = config;
  const list = options || [];
  if (!(groupLabel && groupValues)) {
    return list.map((raw, index) => normalizeOption(raw, String(index), config));
  }
  const entries = [];
  list.forEach((group, groupIndex) => {
    entries.push({
      isGroup: true,
      text: asText(group && group[groupLabel]),
      groupIndex,
      disabled: false,
      key: `group|${groupIndex}`,
    });
    const children = (group && group[groupValues]) || [];
    children.forEach((raw, index) => {
      entries.push({
        ...normalizeOption(raw, `${groupIndex}-${index}`, config),
        groupIndex,
      });
    });
  });
  return entries;
}

// Sortiert die Optionsliste, gruppierte Listen behalten ihre Reihenfolge,
// damit Gruppentitel und Kinder zusammenbleiben
export function sortOptions(options, { sort, hasGroups }) {
  if (sort === 'none' || hasGroups) {
    return options;
  }
  const byText = (a, b) => a.text.localeCompare(b.text, 'de', { numeric: true });
  return [...options].sort(byText);
}

// Stellt die gewählten Optionen an den Anfang
export function pinSelectedFirst(options, { values, enabled }) {
  if (!enabled) {
    return options;
  }
  const snapshot = new Set(values);
  const selected = options.filter(option => snapshot.has(option.value));
  if (!selected.length) {
    return options;
  }
  return [...selected, ...options.filter(option => !snapshot.has(option.value))];
}

// Filtert die Optionen nach dem Suchbegriff, bei internalSearch false bleibt die Liste unverändert
export function filterOptions(options, { search, internalSearch, hasGroups }) {
  const term = normalizeText(search);
  if (!term || !internalSearch) {
    return options;
  }
  const matches = options.filter(option => option.isGroup || normalizeText(option.text).includes(term));
  if (!hasGroups) {
    return matches;
  }
  // Ein Gruppentitel bleibt nur stehen, wenn direkt darunter noch Treffer folgen
  return matches.filter(
    (option, index) => !option.isGroup || (matches[index + 1] && !matches[index + 1].isGroup)
  );
}

const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]';

// Liefert das in Dokumentreihenfolge benachbarte fokussierbare Element außerhalb des Elements
// Innerhalb eines Modals bleibt die Suche auf das Modal beschränkt, da b-modal den Fokus dort hält
export function findAdjacentFocusable(element, backwards) {
  const scope = element.closest('.modal') || document;
  const candidates = Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    candidate =>
      !element.contains(candidate) &&
      !candidate.disabled &&
      candidate.tabIndex >= 0 &&
      candidate.offsetParent !== null
  );
  const wanted = backwards ? Node.DOCUMENT_POSITION_PRECEDING : Node.DOCUMENT_POSITION_FOLLOWING;
  const matches = candidates.filter(candidate => element.compareDocumentPosition(candidate) & wanted);
  return backwards ? matches[matches.length - 1] || null : matches[0] || null;
}

// Klappt einen AppSelect-Trigger auf
export function openAppSelectTrigger(element) {
  if (!element || !element.classList.contains('app-select__toggle')) {
    return false;
  }
  if (element.getAttribute('aria-expanded') === 'true') {
    return false;
  }
  element.click();
  return true;
}
